// src/pages/SearchResults.ts
import "./SearchResults.css";

import type { Country } from "../types/Country";
import type { Municipality } from "../types/Municipality";
import type { NBAPlayer } from "../components/nba/NBAPlayerCard";
import type {
  SearchApiResponse,
  SearchResultItem,
} from "../types/SearchResult";

import {
  renderCountryCard,
  initCountryCardEvents,
} from "../components/countries/CountryCard";
import {
  renderMunicipalityCard,
  initMunicipalityCardEvents,
} from "../components/municipalities/MunicipalityCard";
import {
  renderNBAPlayerCard,
  initNBAPlayerCardEvents,
} from "../components/nba/NBAPlayerCard";

import { renderTimeCard } from "../components/TimeCard";
import { renderWeatherCard } from "../components/WeatherCard";
import { renderLocationPrompt } from "../components/LocationPrompt";
import { renderCalculatorCard } from "../components/CalculatorCard";
import { renderDictionaryCard } from "../components/DictionaryCard";
import { renderUnitConverterCard } from "../components/UnitConverterCard";
import { renderCurrencyConverterCard } from "../components/CurrencyConverterCard";
import { renderAIAnswerBox } from "../components/AIAnswerBox";
import { renderSearchBar } from "../components/SearchBar";
import { renderSearchFooter } from "../components/SearchFooter";
import {
  findAyeOwnedSite,
  isRelevantResultForAyeOwnedSite,
  renderAyeOwnedSiteResult,
} from "../components/AyeOwnedSiteResult";
import {
  type CompanySiteDefinition,
  renderCompanySiteResult,
} from "../components/CompanySiteResult";
import { fetchWebSearch } from "../api/search";

const AI_OVERVIEW_API_URL =
  "https://api.ayesearch.com/api/ai/overview/";

const MICHAEL_JORDAN_WIKIPEDIA_IMAGE =
  "https://images.ayesearch.com/search/c1/c174867e24053e8d718bc562dd8627a63d1203141945f09575346f44057da332.webp";

const MICHAEL_JORDAN_DESCRIPTION =
  "Michael Jeffrey Jordan (born February 17, 1963) is an American businessman and former professional basketball player. He played 15 seasons in the NBA, won six championships with the Chicago Bulls, earned five league MVP awards, and became one of basketball's most influential and widely recognized players.";

const countries: Record<string, Country> = {};
const municipalities: Municipality[] = [];
const nbaPlayers: NBAPlayer[] = [];

const countryFiles = import.meta.glob("../data/countries/*.json", {
  eager: true,
  import: "default",
});

for (const country of Object.values(countryFiles) as Country[]) {
  if (!country || !country.name) continue;
  const slug = country.slug || normalize(country.name);
  countries[slug] = country;
}

const muniFiles = import.meta.glob(
  "../data/japan-municipalities-main/*.json",
  { eager: true, import: "default" },
);

for (const m of Object.values(muniFiles) as Municipality[]) {
  if (m && typeof m.name === "string") {
    municipalities.push(m);
  }
}

import nbaData from "../data/processed/nba-unique-players.json";
nbaPlayers.push(...(nbaData as NBAPlayer[]));

function normalize(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

function normalizeName(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

const OFFICIAL_NAVIGATION_QUERIES = new Set([
  "x",
  "x.com",
  "www.x.com",
  "twitter",
  "twitter.com",
  "www.twitter.com",
]);

function isOfficialNavigationQuery(query: string): boolean {
  const normalized = query
    .toLowerCase()
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

  return OFFICIAL_NAVIGATION_QUERIES.has(normalized);
}

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function focalPercent(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 50;
  }

  return Math.max(0, Math.min(100, value * 100));
}

function isMichaelJordanResult(result: SearchResultItem): boolean {
  const title = (result.title || "").toLowerCase();
  const url = (result.canonical_url || result.url || "").toLowerCase();

  return (
    title.includes("michael jordan") ||
    url.includes("/wiki/michael_jordan")
  );
}

function isMichaelJacksonQuery(query: string): boolean {
  return normalize(query) === "michael-jackson";
}


function tokenizeSearchText(value: string): string[] {
  return String(value || "")
    .toLowerCase()
    .replace(/https?:\/\/(www\.)?/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function isResultRelevantToQuery(
  result: SearchResultItem,
  query: string,
): boolean {
  const queryTokens = tokenizeSearchText(query);

  if (!queryTokens.length) {
    return false;
  }

  const title = tokenizeSearchText(result.title || "");
  const targetUrl = tokenizeSearchText(
    result.canonical_url || result.url || "",
  );

  // Defensive frontend guard: generic web results must match the query
  // in their identity (title/URL), not only in description/body text.
  const identityTokens = new Set([...title, ...targetUrl]);

  return queryTokens.every((token) => identityTokens.has(token));
}

function filterRelevantWebResults(
  results: SearchResultItem[],
  query: string,
): SearchResultItem[] {
  return results.filter((result) =>
    isResultRelevantToQuery(result, query),
  );
}

function renderPeopleAlsoSearchFor(query: string): string {
  if (!isMichaelJacksonQuery(query)) {
    return "";
  }

  return `
    <section class="serp-related-section">
      <div class="serp-section-kicker">PEOPLE ALSO SEARCH FOR</div>

      <a
        class="serp-related-card"
        href="/search?q=Michael%20Jordan"
        aria-label="Search for Michael Jordan"
      >
        <img
          class="serp-related-image"
          src="${MICHAEL_JORDAN_WIKIPEDIA_IMAGE}"
          alt="Michael Jordan"
          loading="lazy"
          referrerpolicy="no-referrer"
        />

        <div class="serp-related-copy">
          <strong>Michael Jordan</strong>
          <span>Basketball player</span>
          <p>${escapeHtml(MICHAEL_JORDAN_DESCRIPTION)}</p>
        </div>
      </a>
    </section>
  `;
}

function formatDisplayUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl);
    const path = url.pathname
      .split("/")
      .filter(Boolean)
      .slice(0, 3)
      .map((part) => decodeURIComponent(part))
      .join(" › ");

    return path ? `${url.hostname} › ${path}` : url.hostname;
  } catch {
    return rawUrl;
  }
}

function sourceLabel(rawUrl: string): string {
  try {
    const host = new URL(rawUrl).hostname.replace(/^www\./, "");

    if (host.includes("wikipedia.org")) return "Wikipedia";
    if (host.includes("forbes.com")) return "Forbes";
    if (host.includes("aikipedia")) return "Aikipedia";

    const first = host.split(".")[0] || host;
    return first.charAt(0).toUpperCase() + first.slice(1);
  } catch {
    return "Web";
  }
}

function resultBadge(result: SearchResultItem): string {
  let host = "";

  try {
    host = new URL(result.canonical_url || result.url).hostname;
  } catch {
    host = "";
  }

  if (host.includes("aikipedia")) return "AYESEARCH";
  if (host.includes("wikipedia.org")) return "REFERENCE";
  if (host.includes("forbes.com")) return "PUBLISHER";
  return "WEB RESULT";
}

function isWeatherQuery(q: string): boolean {
  const w = q.toLowerCase().trim();

  if (w === "weather") return true;

  return (
    w.includes("weather") ||
    w.includes("wheather") ||
    w.includes("what is the weather") ||
    w.includes("what’s the weather") ||
    w.includes("whats the weather") ||
    w.includes("how's the weather") ||
    w.includes("how is the weather") ||
    w.includes("weather today")
  );
}

function classifyQuery(query: string): string {
  const q = query.toLowerCase().trim();

  if (q.includes("time")) return "TIME_CARD";
  if (isWeatherQuery(q)) return "WEATHER_CARD";
  if (/^[0-9+\-*/(). ]+$/.test(q)) return "CALCULATOR_CARD";

  const isDefinitionQuery =
    q.startsWith("define ") ||
    q.startsWith("define:") ||
    q.startsWith("definition ") ||
    q.includes("definition of ");

  if (isDefinitionQuery) return "DICTIONARY_CARD";
  if (q.includes("convert")) return "UNIT_CONVERTER_CARD";
  if (q.includes("currency")) return "CURRENCY_CONVERTER_CARD";

  return "WEB_SEARCH";
}

function detectCountrySlug(query: string): string | null {
  const q = query.trim().toLowerCase();
  const slug = normalize(q);

  if (countries[slug]) return slug;

  for (const [key, country] of Object.entries(countries)) {
    if (country.name.toLowerCase() === q) return key;
  }

  return null;
}

function detectMunicipality(query: string): Municipality | null {
  const lower = query.toLowerCase().trim();

  return (
    municipalities.find(
      (m) =>
        m &&
        typeof m.name === "string" &&
        m.name.toLowerCase() === lower,
    ) || null
  );
}

function rankNBAPlayer(player: NBAPlayer, query: string): number {
  const q = normalizeName(query);
  const name = normalizeName(player.name);
  const slug = player.slug.toLowerCase();

  let score = 0;

  if (slug === normalize(query)) score += 1000;
  if (name === q) score += 900;
  if (name.startsWith(q)) score += 600;
  // Do not use generic substring matching here. It lets surname-only
  // queries such as "forbes" hijack official-company navigation.

  return score;
}

function renderSearchError(container: HTMLElement, query: string): void {
  container.innerHTML = `
    <div class="serp-empty">
      <div class="serp-empty-kicker">AYESEARCH</div>
      <h1>Search is temporarily unavailable</h1>
      <p>
        We couldn't load web results for
        <strong>${escapeHtml(query)}</strong>.
        Please try again.
      </p>
    </div>
  `;
}

function renderEmptyResults(container: HTMLElement, query: string): void {
  container.innerHTML = `
    <div class="serp-empty">
      <div class="serp-empty-kicker">NO RESULTS YET</div>
      <h1>No indexed pages matched “${escapeHtml(query)}”</h1>
      <p>
        AyeSearch is still growing its live web index.
        Try another phrase or check back after more pages are crawled.
      </p>
    </div>
  `;
}

function renderWebResult(
  result: SearchResultItem,
  index: number,
): string {
  const targetUrl = result.canonical_url || result.url;
  const safeUrl = escapeHtml(targetUrl);
  const jordanResult = isMichaelJordanResult(result);
  const safeTitle = escapeHtml(result.title || targetUrl);
  const safeDescription = escapeHtml(
    jordanResult
      ? MICHAEL_JORDAN_DESCRIPTION
      : result.description || "",
  );

  const sourceName =
    result.source_name ||
    sourceLabel(targetUrl);

  const safeSource = escapeHtml(sourceName);
  const safeDisplayUrl = escapeHtml(formatDisplayUrl(targetUrl));
  const safeBadge = escapeHtml(resultBadge(result));
  const focalX = focalPercent(result.image_focal_x);
  const focalY = focalPercent(result.image_focal_y);

  const faviconUrl =
    result.favicon_url ||
    result.favicon_source_url ||
    "";

  const displayImageUrl = jordanResult
    ? MICHAEL_JORDAN_WIKIPEDIA_IMAGE
    : result.image_url;

  const sourceIconHtml = faviconUrl
    ? `
      <img
        class="serp-source-favicon"
        src="${escapeHtml(faviconUrl)}"
        alt=""
        loading="${index === 0 ? "eager" : "lazy"}"
        referrerpolicy="no-referrer"
        data-fallback-letter="${escapeHtml(
          safeSource.slice(0, 1).toUpperCase(),
        )}"
      />
    `
    : `
      <span class="serp-source-letter">
        ${safeSource.slice(0, 1).toUpperCase()}
      </span>
    `;

  const imageHtml = displayImageUrl
    ? `
      <a
        class="serp-result-image-link"
        href="${safeUrl}"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open ${safeTitle}"
      >
        <img
          class="serp-result-image"
          src="${escapeHtml(displayImageUrl)}"
          alt=""
          loading="${index === 0 ? "eager" : "lazy"}"
          referrerpolicy="no-referrer"
          style="object-position: ${focalX.toFixed(2)}% ${focalY.toFixed(2)}%;"
        />
      </a>
    `
    : "";

  return `
    <article class="serp-result ${displayImageUrl ? "has-image" : ""}">
      <div class="serp-result-copy">
        <div class="serp-result-badge">${safeBadge}</div>

        <a
          class="serp-source-row"
          href="${safeUrl}"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span class="serp-source-mark">
            ${sourceIconHtml}
          </span>

          <span class="serp-source-meta">
            <strong>${safeSource}</strong>
            <span>${safeDisplayUrl}</span>
          </span>
        </a>

        <h2 class="serp-result-title">
          <a
            href="${safeUrl}"
            target="_blank"
            rel="noopener noreferrer"
          >
            ${safeTitle}
          </a>
        </h2>

        ${
          safeDescription
            ? `<p class="serp-result-snippet">${safeDescription}</p>`
            : ""
        }
      </div>

      ${imageHtml}
    </article>
  `;
}

function renderWebSearchResults(
  container: HTMLElement,
  response: SearchApiResponse,
): void {
  const queryRelevantResults = filterRelevantWebResults(
    response.results,
    response.query,
  );

  const visibleResults = isMichaelJacksonQuery(response.query)
    ? queryRelevantResults.filter((result) => !isMichaelJordanResult(result))
    : queryRelevantResults;

  if (!visibleResults.length) {
    renderEmptyResults(container, response.query);
    return;
  }

  const visibleCount = visibleResults.length;
  const countLabel =
    visibleCount === 1
      ? "1 indexed result"
      : `${visibleCount.toLocaleString()} indexed results`;

  container.innerHTML = `
    <div class="serp-results-meta">
      <span>${escapeHtml(countLabel)}</span>
      <span class="serp-results-dot">•</span>
      <span>Live AyeSearch index</span>
    </div>

    <div class="serp-results-feed">
      ${visibleResults.map(renderWebResult).join("")}
    </div>
  `;

  const favicons = container.querySelectorAll<HTMLImageElement>(
    ".serp-source-favicon",
  );

  favicons.forEach((favicon) => {
    favicon.addEventListener("error", () => {
      const mark = favicon.closest(".serp-source-mark");
      if (!mark) return;

      const fallbackLetter =
        favicon.dataset.fallbackLetter ||
        "?";

      mark.innerHTML = `
        <span class="serp-source-letter">
          ${escapeHtml(fallbackLetter)}
        </span>
      `;
    });
  });

  const images = container.querySelectorAll<HTMLImageElement>(
    ".serp-result-image",
  );

  images.forEach((image) => {
    image.addEventListener("error", () => {
      const imageLink = image.closest(".serp-result-image-link");
      imageLink?.remove();

      const result = image.closest(".serp-result");
      result?.classList.remove("has-image");
    });
  });
}

async function loadWebResults(
  container: HTMLElement,
  query: string,
  featuredContainer?: HTMLElement | null,
  aiContainer?: HTMLElement | null,
  aiSection?: HTMLElement | null,
): Promise<void> {
  try {
    const response = await fetchWebSearch(query);
    const ayeOwnedSite = findAyeOwnedSite(query);

    const apiResponse = response as SearchApiResponse & {
      official_site?: CompanySiteDefinition | null;
      domain_media?: {
        domain?: string;
        url?: string;
        canonical_url?: string;
        favicon_url?: string;
        favicon_source_url?: string;
        image_url?: string;
        fetched_at?: string | null;
        indexed_at?: string | null;
      } | null;
    };

    const officialSite = apiResponse.official_site || null;
    const domainMedia = apiResponse.domain_media || null;

    // External company results intentionally do not show or request AI Overview.
    // Non-company searches reveal and load AI only after the backend response
    // confirms there is no official company result.
    if (officialSite) {
      aiSection?.remove();
    } else if (aiSection && aiContainer) {
      aiSection.removeAttribute("hidden");
      void loadAiOverview(aiContainer, query);
    }

    if (ayeOwnedSite) {
      const relevantResults = response.results.filter(
        (result) =>
          isRelevantResultForAyeOwnedSite(
            ayeOwnedSite,
            result,
          ),
      );

      const primaryResult = relevantResults[0];

      if (featuredContainer) {
        const featuredSection =
          document.getElementById(
            "serp-featured-site-section",
          );

        featuredSection?.removeAttribute("hidden");

        featuredContainer.innerHTML =
          renderAyeOwnedSiteResult(
            ayeOwnedSite,
            {
              faviconUrl:
                domainMedia?.favicon_url ||
                domainMedia?.favicon_source_url ||
                primaryResult?.favicon_url ||
                primaryResult?.favicon_source_url ||
                "",
              imageUrl:
                domainMedia?.image_url ||
                primaryResult?.image_url ||
                "",
            },
          );
      }

      if (!relevantResults.length) {
        container.innerHTML = `
          <div class="serp-web-loading-note">
            No additional relevant indexed pages yet.
          </div>
        `;
        return;
      }

      renderWebSearchResults(
        container,
        {
          ...response,
          count: relevantResults.length,
          results: relevantResults,
        },
      );
      return;
    }

    if (officialSite) {
      if (featuredContainer) {
        const featuredSection =
          document.getElementById(
            "serp-featured-site-section",
          );

        featuredSection?.removeAttribute("hidden");
        featuredContainer.innerHTML =
          renderCompanySiteResult(officialSite);
      }

      const officialDomain = (
        officialSite.domain || ""
      )
        .toLowerCase()
        .replace(/^www\./, "");

      const resultDomains = Array.from(
        new Set(
          [
            officialDomain,
            ...(Array.isArray(officialSite.result_domains)
              ? officialSite.result_domains
              : []),
          ]
            .map((domain) =>
              String(domain || "")
                .toLowerCase()
                .trim()
                .replace(/^https?:\/\//, "")
                .replace(/^www\./, "")
                .replace(/\/.*$/, ""),
            )
            .filter(Boolean),
        ),
      );

      const domainRank = (result: SearchResultItem): number => {
        try {
          const targetUrl =
            result.canonical_url ||
            result.url ||
            "";

          const host = new URL(targetUrl).hostname
            .toLowerCase()
            .replace(/^www\./, "");

          const rank = resultDomains.findIndex(
            (domain) =>
              host === domain ||
              host.endsWith(`.${domain}`),
          );

          return rank === -1
            ? Number.MAX_SAFE_INTEGER
            : rank;
        } catch {
          return Number.MAX_SAFE_INTEGER;
        }
      };

      const relevantResults = response.results
        .filter((result) => domainRank(result) !== Number.MAX_SAFE_INTEGER)
        .sort((a, b) => domainRank(a) - domainRank(b));

      if (!relevantResults.length) {
        container.innerHTML = `
          <div class="serp-web-loading-note">
            No additional relevant indexed pages yet.
          </div>
        `;
        return;
      }

      renderWebSearchResults(
        container,
        {
          ...response,
          count: relevantResults.length,
          results: relevantResults,
        },
      );
      return;
    }

    renderWebSearchResults(container, response);
  } catch (error) {
    console.error("AyeSearch web search failed:", error);
    renderSearchError(container, query);
  }
}

async function loadAiOverview(
  container: HTMLElement,
  query: string,
): Promise<void> {
  container.innerHTML = `
    <div class="serp-ai-loading">
      Generating AI overview for <strong>${escapeHtml(query)}</strong>...
    </div>
  `;

  try {
    const url = new URL(AI_OVERVIEW_API_URL);
    url.searchParams.set("q", query);

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`AI Overview returned HTTP ${response.status}`);
    }

    const data = await response.json();

    if (!data) {
      throw new Error("Empty AI overview response");
    }

    renderAIAnswerBox(container, data);
  } catch (error) {
    console.error("AyeSearch AI overview failed:", error);
    container.innerHTML = `
      <div class="serp-ai-error">
        AI overview is temporarily unavailable for
        <strong>${escapeHtml(query)}</strong>.
      </div>
    `;
  }
}

function renderRealSearchExperience(
  main: HTMLElement,
  query: string,
): void {
  main.innerHTML = `
    <section class="serp-shell">
      <div class="serp-query-summary">
        Showing AyeSearch results for <strong>${escapeHtml(query)}</strong>
      </div>

      <section
        class="serp-section serp-owned-site-section"
        id="serp-featured-site-section"
        hidden
      >
        <div id="serp-featured-site"></div>
      </section>

      <section
        class="serp-section serp-ai-section"
        id="serp-ai-section"
        hidden
      >
        <div class="serp-section-kicker">AI OVERVIEW</div>
        <div class="serp-ai-card" id="serp-ai-overview"></div>
      </section>

      <section class="serp-section serp-web-section">
        <div class="serp-section-kicker">WEB RESULTS</div>
        <div id="serp-web-results">
          <div class="serp-loading-list">
            <div class="serp-web-loading-note">
              Loading live indexed results...
            </div>
            <div class="serp-skeleton"></div>
            <div class="serp-skeleton"></div>
            <div class="serp-skeleton"></div>
          </div>
        </div>
      </section>

      ${renderPeopleAlsoSearchFor(query)}
    </section>
  `;

  const aiContainer = document.getElementById("serp-ai-overview");
  const aiSection = document.getElementById("serp-ai-section");
  const webContainer = document.getElementById("serp-web-results");
  const featuredContainer =
    document.getElementById("serp-featured-site");

  // Load web results first so the backend can decide whether this is a
  // curated official-company result. Company results never start AI Overview.
  if (webContainer) {
    void loadWebResults(
      webContainer,
      query,
      featuredContainer,
      aiContainer,
      aiSection,
    );
  }
}

export function renderSearchResults(query: string = ""): void {
  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => renderSearchResults(query),
      { once: true },
    );
    return;
  }

  const app = document.getElementById("app");
  if (!app) return;

  const normalizedQuery = normalize(query);

  app.innerHTML = `
    <div class="results-container">
      <div
        id="search-bar-container"
        class="searchbar-container"
      ></div>
      <main class="results-main" id="results-main"></main>
      <div id="search-footer-container"></div>
    </div>
  `;

  const main = document.getElementById("results-main")!;
  const searchBarContainer = document.getElementById(
    "search-bar-container",
  )!;

  renderSearchBar(searchBarContainer, query);

  const footerContainer = document.getElementById(
    "search-footer-container",
  );
  if (footerContainer) {
    renderSearchFooter(footerContainer);
  }

  if (!query.trim()) {
    renderEmptyResults(main, query);
    return;
  }

  const queryType = classifyQuery(query);

  if (queryType === "TIME_CARD") {
    renderTimeCard(main);
    return;
  }

  if (queryType === "WEATHER_CARD") {
    const stored = localStorage.getItem("ayesearch_user_location");

    if (!stored && !query.toLowerCase().includes("weather in")) {
      renderLocationPrompt(main, () => renderWeatherCard(main, query));
      return;
    }

    renderWeatherCard(main, query);
    return;
  }

  if (queryType === "CALCULATOR_CARD") {
    renderCalculatorCard(main, query);
    return;
  }

  if (queryType === "DICTIONARY_CARD") {
    renderDictionaryCard(main, query);
    return;
  }

  if (queryType === "UNIT_CONVERTER_CARD") {
    renderUnitConverterCard(main, query);
    return;
  }

  if (queryType === "CURRENCY_CONVERTER_CARD") {
    renderCurrencyConverterCard(main, query);
    return;
  }

  const countrySlug = detectCountrySlug(query);

  if (countrySlug) {
    const country = countries[countrySlug];

    if (country) {
      const wrapper = document.createElement("div");
      wrapper.className = "country-result-wrapper";
      wrapper.innerHTML = renderCountryCard(country);
      main.appendChild(wrapper);
      initCountryCardEvents(wrapper);
      return;
    }
  }

  const municipality = detectMunicipality(query);

  if (municipality) {
    const wrapper = document.createElement("div");
    wrapper.className = "municipality-result-wrapper";
    wrapper.innerHTML = renderMunicipalityCard(municipality);
    main.appendChild(wrapper);
    initMunicipalityCardEvents(wrapper);
    return;
  }

  // Exact navigational aliases such as "x" must use the official-site
  // pipeline instead of fuzzy-matching an NBA player such as Xavier Cooks.
  if (isOfficialNavigationQuery(query)) {
    renderRealSearchExperience(main, query);
    return;
  }

  // Michael Jackson stays on the real crawler/index SERP so an NBA
  // namesake cannot replace the singer. Michael Jordan remains eligible
  // for the renovated NBA player card.
  if (normalizedQuery !== "michael-jackson") {
    const ranked = nbaPlayers
      .map((player) => ({
        player,
        score: rankNBAPlayer(player, query),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score);

    if (ranked.length > 0) {
      const top = ranked[0].player;
      const wrapper = document.createElement("div");
      wrapper.className = "nba-result-wrapper";
      wrapper.innerHTML = renderNBAPlayerCard(top, true);
      main.appendChild(wrapper);
      initNBAPlayerCardEvents(wrapper);
      return;
    }
  }

  renderRealSearchExperience(main, query);
}
